import React from "react";
import { Box, Typography } from "@mui/material";
import PageContainer from "../../components/PageContainer";
import PageSection from "../../components/PageSection";
import NoticeboardButton from "../../components/shared/NoticeboardButton";
import {
  COURSE_MODULES,
  LESSONS,
  lessonPath,
  readMinutes,
  writtenModules,
} from "../../domain/help/course";
import { useCourseProgress } from "../../hooks/useCourseProgress";
import { HELP_VIDEOS_PATH } from "../../domain/help/helpContent";
import ForAdminsTag from "./ForAdminsTag";
import {
  CourseBreadcrumbs,
  CourseFinished,
  CourseHero,
  CourseLessonCard,
  CourseModule,
  NextLessonBox,
} from "./CourseOverviewParts";

const CourseCertificateButton = React.lazy(
  () => import("./CourseCertificateButton"),
);

const syncMessage = {
  saved: "Your progress is saved to your account.",
  checking: "Checking your saved progress…",
  saving: "Saving your progress…",
  offline: "Your progress is kept in this browser. Account sync is unavailable.",
};

const CourseHome: React.FC = () => {
  const { finished, doneCount, nextUp, memberId, finishedOn, progressReady, syncStatus, retrySync } =
    useCourseProgress();

  return (
    <PageContainer>
      <PageSection sx={{ mb: 4 }}>
        <CourseBreadcrumbs
          crumbs={[
            { label: "Help Center", to: HELP_VIDEOS_PATH },
            { label: "Noticeboard Fundamentals" },
          ]}
        />
        <CourseHero
          accent="Fundamentals"
          description="Short lessons that take you from setting up your Organization to sending your first Notice. Work through them at your own pace."
          doneCount={doneCount}
          total={LESSONS.length}
          progressLabel="Course progress"
          image="/icons/dashboard.webp"
          progressNote={
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {syncMessage[syncStatus as keyof typeof syncMessage] ?? syncMessage.offline}
              </Typography>
              {syncStatus === "offline" && (
                <NoticeboardButton variant="tertiary" onClick={retrySync}>
                  Retry sync
                </NoticeboardButton>
              )}
            </>
          }
        >
          {!progressReady && nextUp ? (
            <Typography role="status" color="text.secondary">
              {syncStatus === "checking"
                ? "Loading your course progress…"
                : "Retry sync to check your saved course progress."}
            </Typography>
          ) : nextUp ? (
            <NextLessonBox
              first={doneCount === 0}
              title={nextUp.title}
              meta={`Lesson ${nextUp.number} · ${readMinutes(nextUp)} min read`}
              to={lessonPath(nextUp)}
              buttonLabel={doneCount === 0 ? "Start" : `Continue lesson ${nextUp.number}`}
            />
          ) : (
            finishedOn && (
              <CourseFinished>
                <React.Suspense fallback={null}>
                  <CourseCertificateButton memberId={memberId} finishedOn={finishedOn} />
                </React.Suspense>
              </CourseFinished>
            )
          )}
        </CourseHero>
      </PageSection>

      {writtenModules().map(({ module, lessons }) => (
        <PageSection key={module} sx={{ mb: 4 }}>
          <CourseModule
            number={COURSE_MODULES.indexOf(module) + 1}
            title={module}
            doneCount={lessons.filter((lesson) => finished.has(lesson.slug)).length}
            total={lessons.length}
          >
            {lessons.map((lesson) => (
              <CourseLessonCard
                key={lesson.slug}
                number={lesson.number}
                title={lesson.title}
                intro={lesson.intro}
                to={lessonPath(lesson)}
                done={finished.has(lesson.slug)}
                next={progressReady && nextUp?.slug === lesson.slug}
                footer={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" color="text.secondary">
                      {readMinutes(lesson)} min read
                    </Typography>
                    {lesson.forAdmins && <ForAdminsTag />}
                  </Box>
                }
              />
            ))}
          </CourseModule>
        </PageSection>
      ))}
    </PageContainer>
  );
};

export default CourseHome;
